import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Activity, ChevronRight, FileText, Stethoscope } from 'lucide-react';
import './NavCategoryHub.css';

/* ------------------------------------------------------------------ */
/*  DETAILED VECTOR ICONS MATCHING THE WIREFRAME PHOTO EXACTLY         */
/* ------------------------------------------------------------------ */

// 1. Red Graduation Cap Mortarboard
const CapIcon = () => (
  <svg viewBox="0 0 100 100" className="hub-card-svg" aria-hidden="true">
    <defs>
      <linearGradient id="capTopGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#d1002c" />
        <stop offset="55%" stopColor="#b40026" />
        <stop offset="100%" stopColor="#800018" />
      </linearGradient>
      <linearGradient id="capUnderGrad" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#800018" />
        <stop offset="100%" stopColor="#550010" />
      </linearGradient>
      <filter id="capShadow" x="-15%" y="-15%" width="130%" height="130%">
        <feDropShadow dx="0" dy="4" stdDeviation="3" floodColor="#b40026" floodOpacity="0.3" />
      </filter>
    </defs>
    {/* Cap skull cap under the diamond */}
    <path
      d="M28 54 C28 66 38 74 50 74 C62 74 72 66 72 54 L72 50 L28 50 Z"
      fill="url(#capUnderGrad)"
    />
    {/* Diamond mortarboard top */}
    <path
      d="M50 26 L86 42 L50 58 L14 42 Z"
      fill="url(#capTopGrad)"
      filter="url(#capShadow)"
    />
    {/* White highlight rim on left facet */}
    <path
      d="M50 26 L14 42 L50 58"
      fill="none"
      stroke="rgba(255,255,255,0.45)"
      strokeWidth="1.2"
    />
    {/* Center button */}
    <ellipse cx="50" cy="42" rx="3.5" ry="2.5" fill="#f8d7da" />
    {/* Tassel cord and fringe */}
    <path
      d="M50 42 Q68 44 75 52"
      fill="none"
      stroke="#2c3038"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <rect x="73" y="52" width="4.5" height="12" rx="2" fill="#2c3038" />
  </svg>
);

// 2. Newspaper with red folded corner & red image block
const NewsIcon = () => (
  <svg viewBox="0 0 100 100" className="hub-card-svg" aria-hidden="true">
    <defs>
      <filter id="newsShadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000000" floodOpacity="0.12" />
      </filter>
    </defs>
    {/* Back folded page edge */}
    <path
      d="M72 32 L81 40 L81 72 C81 74 79 76 77 76 L72 76 Z"
      fill="#b40026"
      opacity="0.85"
    />
    {/* Main newspaper sheet */}
    <rect
      x="22"
      y="22"
      width="50"
      height="56"
      rx="6"
      fill="#ffffff"
      stroke="#374151"
      strokeWidth="3.5"
      filter="url(#newsShadow)"
    />
    {/* Red image / header box */}
    <rect x="29" y="30" width="16" height="14" rx="2.5" fill="#b40026" />
    {/* Headline lines */}
    <line x1="49" y1="32" x2="65" y2="32" stroke="#374151" strokeWidth="3" strokeLinecap="round" />
    <line x1="49" y1="38" x2="65" y2="38" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="49" y1="44" x2="61" y2="44" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" />
    {/* Body text lines */}
    <line x1="29" y1="52" x2="65" y2="52" stroke="#374151" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="29" y1="58" x2="65" y2="58" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
    <line x1="29" y1="64" x2="65" y2="64" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
    <line x1="29" y1="70" x2="52" y2="70" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// 3. Doctor with Stethoscope & Red Chat Bubble (Consultation)
const ConsultationIcon = () => (
  <svg viewBox="0 0 100 100" className="hub-card-svg" aria-hidden="true">
    <defs>
      <filter id="consultShadow" x="-10%" y="-10%" width="125%" height="125%">
        <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#b40026" floodOpacity="0.2" />
      </filter>
    </defs>
    {/* Doctor silhouette head */}
    <circle cx="44" cy="35" r="11" fill="#374151" />
    {/* Doctor shoulders */}
    <path
      d="M25 68 C25 54 33 49 44 49 C55 49 63 54 63 68 Z"
      fill="#374151"
    />
    {/* Stethoscope around neck */}
    <path
      d="M34 52 C34 62 40 68 47 68 C52 68 56 64 56 57"
      fill="none"
      stroke="#b40026"
      strokeWidth="3.2"
      strokeLinecap="round"
    />
    {/* Stethoscope bell */}
    <circle cx="56" cy="57" r="3.2" fill="#d1002c" />
    <circle cx="56" cy="57" r="1.2" fill="#ffffff" />
    {/* Red Speech Bubble on the right with 3 dots */}
    <g filter="url(#consultShadow)">
      <rect x="53" y="19" width="31" height="22" rx="7" fill="#b40026" />
      <polygon points="58,40 54,47 65,40" fill="#b40026" />
      {/* 3 white dots */}
      <circle cx="61" cy="30" r="2" fill="#ffffff" />
      <circle cx="68.5" cy="30" r="2" fill="#ffffff" />
      <circle cx="76" cy="30" r="2" fill="#ffffff" />
    </g>
  </svg>
);

// 4. Calculator Icon with LCD display and calculation keys
const CalculatorIcon = () => (
  <svg viewBox="0 0 100 100" className="hub-card-svg" aria-hidden="true">
    <defs>
      <filter id="calcShadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000000" floodOpacity="0.12" />
      </filter>
    </defs>
    {/* Calculator frame */}
    <rect
      x="28"
      y="20"
      width="44"
      height="60"
      rx="7"
      fill="#ffffff"
      stroke="#374151"
      strokeWidth="3.5"
      filter="url(#calcShadow)"
    />
    {/* Screen */}
    <rect x="34" y="27" width="32" height="13" rx="3" fill="#e5e7eb" stroke="#cbd5e1" strokeWidth="1" />
    <text x="62" y="37" textAnchor="end" fontSize="8" fontWeight="bold" fill="#1f2937" fontFamily="monospace">
      128.5
    </text>
    {/* Buttons grid */}
    <circle cx="39" cy="48" r="3" fill="#9ca3af" />
    <circle cx="50" cy="48" r="3" fill="#9ca3af" />
    <circle cx="61" cy="48" r="3" fill="#b40026" />

    <circle cx="39" cy="58" r="3" fill="#9ca3af" />
    <circle cx="50" cy="58" r="3" fill="#9ca3af" />
    <circle cx="61" cy="58" r="3" fill="#b40026" />

    <circle cx="39" cy="68" r="3" fill="#9ca3af" />
    <circle cx="50" cy="68" r="3" fill="#9ca3af" />
    <rect x="58" y="65" width="6" height="6" rx="2" fill="#b40026" />
  </svg>
);

// 5. Market Shopping Cart with Medical Cross
const MarketIcon = () => (
  <svg viewBox="0 0 100 100" className="hub-card-svg" aria-hidden="true">
    <defs>
      <linearGradient id="cartGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#d1002c" />
        <stop offset="60%" stopColor="#b40026" />
        <stop offset="100%" stopColor="#800018" />
      </linearGradient>
      <filter id="cartShadow" x="-10%" y="-10%" width="125%" height="125%">
        <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#b40026" floodOpacity="0.25" />
      </filter>
    </defs>
    {/* Cart handle & frame */}
    <path
      d="M20 27 L28 27 L39 60 L68 60"
      fill="none"
      stroke="#374151"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Wheels */}
    <circle cx="41" cy="68" r="4.5" fill="#374151" />
    <circle cx="41" cy="68" r="1.5" fill="#ffffff" />
    <circle cx="66" cy="68" r="4.5" fill="#374151" />
    <circle cx="66" cy="68" r="1.5" fill="#ffffff" />
    {/* Red Basket */}
    <path
      d="M32 34 L77 34 L70 55 L37 55 Z"
      fill="url(#cartGrad)"
      filter="url(#cartShadow)"
    />
    {/* White Medical Cross in center of basket */}
    <rect x="51.5" y="40" width="4" height="10" rx="1" fill="#ffffff" />
    <rect x="48.5" y="43" width="10" height="4" rx="1" fill="#ffffff" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  CATEGORY HUB COMPONENT                                             */
/* ------------------------------------------------------------------ */

export default function NavCategoryHub() {
  const navigate = useNavigate();
  const [learningMenuOpen, setLearningMenuOpen] = useState(false);

  const categories = [
    {
      id: 'learning',
      label: 'LEARNING',
      Icon: CapIcon,
      hasDropdown: true,
      onClick: () => setLearningMenuOpen((v) => !v),
    },
    {
      id: 'news',
      label: 'NEWS AND ARTICLE',
      Icon: NewsIcon,
      to: '/articles',
    },
    {
      id: 'consultation',
      label: 'CONSULTATION',
      Icon: ConsultationIcon,
      to: '/dashboard?tab=channeling',
    },
    {
      id: 'calculator',
      label: 'CALCULATORS',
      Icon: CalculatorIcon,
      to: '/calculators',
    },
    {
      id: 'market',
      label: 'MARKET',
      Icon: MarketIcon,
      to: '/library',
    },
  ];

  return (
    <section className="dp-hub-wrapper" aria-label="Quick Category Hub">
      <div className="dp-hub-container">
        <div className="dp-hub-row">
          {categories.map((cat) => {
            const content = (
              <>
                <div className="dp-hub-disc" data-testid={`hub-disc-${cat.id}`}>
                  <cat.Icon />
                </div>
                <div className="dp-hub-label-wrap">
                  <span className="dp-hub-label">{cat.label}</span>
                  <span className="dp-hub-underline" aria-hidden="true" />
                </div>
              </>
            );

            if (cat.hasDropdown) {
              return (
                <div key={cat.id} className="dp-hub-card-wrap">
                  <button
                    type="button"
                    className="dp-hub-card"
                    onClick={cat.onClick}
                    aria-expanded={learningMenuOpen}
                  >
                    {content}
                  </button>
                  {learningMenuOpen && (
                    <div className="dp-hub-dropdown">
                      <div className="dp-hub-dropdown-title">Medical Learnings</div>
                      <Link
                        to="/anatomy"
                        className="dp-hub-dropdown-item"
                        onClick={() => setLearningMenuOpen(false)}
                      >
                        <Activity className="w-4 h-4 text-[#B40026]" />
                        <span>Anatomy Modules</span>
                        <ChevronRight className="w-4 h-4 dp-hub-arrow" />
                      </Link>
                      <Link
                        to="/pharma"
                        className="dp-hub-dropdown-item"
                        onClick={() => setLearningMenuOpen(false)}
                      >
                        <FileText className="w-4 h-4 text-[#B40026]" />
                        <span>Pharmacology & Drugs</span>
                        <ChevronRight className="w-4 h-4 dp-hub-arrow" />
                      </Link>
                      <Link
                        to="/diseases"
                        className="dp-hub-dropdown-item"
                        onClick={() => setLearningMenuOpen(false)}
                      >
                        <Stethoscope className="w-4 h-4 text-[#B40026]" />
                        <span>Clinical Diseases</span>
                        <ChevronRight className="w-4 h-4 dp-hub-arrow" />
                      </Link>
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={cat.id} className="dp-hub-card-wrap">
                <Link to={cat.to} className="dp-hub-card">
                  {content}
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
